import { ListItemMoreButton } from '@components/ListItemMoreButton';
import { sendWarning } from '@components/Noti';
import { TipWrapper } from '@components/TipWrapper';
import IconDuplicate from '@components/icons/IconDuplicate';
import IconLocked from '@components/icons/IconLocked';
import { $curScene, $sceneMapApi } from '@stores/dataStores';
import { $editingHotspotApi } from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedScene } from '@stores/loadedStore';
import {
  type GeneralID,
  HSIconType,
  HSPosType,
  type Hotspot,
  SceneType,
} from '@types';
import produce from 'immer';
import { nanoid } from 'nanoid';
import { flagToBool } from 'pano-utils';
import { SortableElement } from 'react-sortable-hoc';
import { deleteHotspots } from '../actions/deleteHotspots';
import {
  getHotspotTitleId,
  krpAddHotspot,
  krpRemoveHotspot,
  toggleHotspotSelected,
} from '../index';
import ItemIcon from './ItemIcon';
import getFeatName from './getFeatName';

interface Props {
  data: Hotspot;
  sceneId: GeneralID;
  listIndex: number;
  isMax: boolean;
  sceneType: SceneType;
}

function HotspotItem({ data, sceneId, listIndex, isMax, sceneType }: Props) {
  const handleClick = () => {
    $editingHotspotApi.set({
      hotspot: data,
      sceneId,
      sceneType,
    });
    MainPlayer.get()?.lookToHotspot({ id: data.id });
    toggleHotspotSelected({
      id: data.id,
      isOn: true,
    });
  };

  const handleDeleteClick = () => {
    deleteHotspots({
      sceneId,
      hotspotIds: data.id,
    });
    krpRemoveHotspot(data.id);
  };

  const handleDuplicateClick = () => {
    if (isMax) {
      sendWarning('复制热点失败，当前场景热点数超过上限');
      return;
    }
    if (
      (data.icon.type === HSIconType.image ||
        data.icon.type === HSIconType.video ||
        data.icon.type === HSIconType.seqFrame) &&
      data.icon.positionType === HSPosType.adv
    ) {
      sendWarning('当前热点图标位置对齐固定，不支持复制');
      return;
    }
    const player = MainPlayer.get();
    const scene = $loadedScene.getState();
    if (!player || !scene) return;
    const isMatrix = $curScene.getState()?.type === SceneType.matrix;
    const newHotspot = produce(data, (draft) => {
      draft.id = `h_${nanoid()}`;
      const { icon } = draft;
      let offsetH = 10;
      let offsetV = 10;
      if (isMatrix) {
        const hfov = Number(scene.matrixFov.hfov);
        const vfov = Number(scene.matrixFov.vfov);
        if (hfov > vfov) {
          offsetH = 0.05 * (vfov / hfov) * player.getView().fov;
          offsetV = 0.05 * player.getView().fov;
        } else {
          offsetH = 0.05 * player.getView().fov;
          offsetV = 0.05 * (hfov / vfov) * player.getView().fov;
        }
      }
      icon.ath = icon.ath! + offsetH;
      icon.atv = icon.atv! + offsetV;
      if (icon.type === HSIconType.polygon) {
        icon.points!.forEach((x) => {
          x.ath += offsetH;
          x.atv += offsetV;
        });
      }
    });
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.hotspot!.splice(listIndex + 1, 0, newHotspot);
        draft.hotspot! = draft.hotspot!.map((x, i) => ({ ...x, zOrder: i }));
      },
    });
    krpAddHotspot(newHotspot);
    player.lookToHotspot({ id: newHotspot.id });
  };

  const handleToggleVisibleClick = () => {
    const player = MainPlayer.get();
    const scene = $loadedScene.getState();
    if (!player || !scene) return;
    const newValue = data.visible === 0 ? 1 : 0;
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.hotspot!.find((x) => x.id === data.id)!.visible = newValue;
      },
    });
    player.updateHotspot({
      id: data.id,
      visible: flagToBool(newValue),
      enabled: flagToBool(newValue),
    });
    if (data.showTitle) {
      const titleId = getHotspotTitleId(data.id);
      player.updateHotspot({
        id: titleId,
        visible: flagToBool(newValue),
        enabled: flagToBool(newValue),
      });
    }
  };

  const handleToggleLockClick = () => {
    const player = MainPlayer.get();
    const scene = $loadedScene.getState();
    if (!player || !scene) return;
    const newValue = data.locked === 1 ? 0 : 1;
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.hotspot!.find((x) => x.id === data.id)!.locked = newValue;
      },
    });
    player.updateHotspot({
      id: data.id,
      dragable: !flagToBool(newValue),
    });
  };

  return (
    <div className="relative">
      {data.locked === 1 && (
        <IconLocked className="absolute top-2 left-2 scale-75" />
      )}
      <div
        className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10"
        style={{ border: data.visible === 0 ? '1px dashed gray' : '' }}
        onClick={handleClick}
      >
        <ItemIcon icon={data.icon} />
        <div className="mx-10 truncate text-base text-white">{data.title}</div>
        <div className="ml-auto flex-shrink-0 text-sm text-black-gray">
          {getFeatName(data.data.code)}
        </div>
        <TipWrapper text="复制" placement="bottom" className="ml-10" size="sm">
          <IconDuplicate
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateClick();
            }}
          />
        </TipWrapper>
        <ListItemMoreButton
          items={
            [
              data.locked === 1 ? '解锁' : '锁定',
              data.visible === 0 ? '显示' : '隐藏',
              '删除',
            ] as const
          }
          onSelect={async (val) => {
            if (val === '解锁' || val === '锁定') {
              handleToggleLockClick();
            } else if (val === '隐藏' || val === '显示') {
              handleToggleVisibleClick();
            } else if (val === '删除') {
              handleDeleteClick();
            }
          }}
          className="ml-10"
        />
      </div>
    </div>
  );
}

export default SortableElement(HotspotItem);
